// =============================================================================
// ConfirmProvider / useConfirm — the app's styled replacement for window.confirm.
//
// WHY: every destructive patient action confirmed through a NATIVE browser dialog
// (26 call sites across 15 components, found by the 2026-08-09 Prod surface audit).
// Native dialogs are unstyled and unbranded against the D4 Prestige system, and on iOS —
// which is the beta cohort, since Android HHS is unbuilt — an installed PWA renders
// confirm() as a system sheet stamped with the origin, which reads as a browser security
// prompt rather than part of the app.
//
// DESIGN: a promise-based imperative API, deliberately. The call sites are all
// `if (window.confirm(msg)) { ... }` — synchronous branches. A declarative per-component
// modal would mean new state, new JSX and a restructured handler in all 26 places. This
// keeps each conversion to one line:
//
//     const confirm = useConfirm();
//     if (!(await confirm({ title, message, confirmLabel, destructive: true }))) return;
//
// The dialog resolves false on cancel, Escape, backdrop click and unmount, so a caller can
// never hang waiting on a promise that is never settled.
// =============================================================================
import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import { useEscapeToClose } from '../hooks/useEscapeToClose';

const ConfirmContext = createContext(null);

/**
 * @returns {(opts: {
 *   title?: string, message: string, confirmLabel?: string, cancelLabel?: string,
 *   destructive?: boolean, requireText?: string
 * }) => Promise<boolean>}
 */
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fail loudly in dev rather than silently returning false and swallowing a delete —
    // a confirm that always says "no" looks like a dead button.
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);   // { opts, resolve } | null
  const [typed, setTyped] = useState('');
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const r = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    setTyped('');
    if (r) r(value);
  }, []);

  const confirm = useCallback((opts) => {
    // If one confirm is somehow open when another is requested, resolve the first as
    // cancelled rather than orphaning its promise.
    if (resolverRef.current) resolverRef.current(false);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setTyped('');
      setState({ opts: typeof opts === 'string' ? { message: opts } : (opts || {}) });
    });
  }, []);

  // A provider unmount must not leave a caller awaiting forever.
  useEffect(() => () => { if (resolverRef.current) resolverRef.current(false); }, []);

  // Stable identity — a fresh arrow each render would tear down and re-add the keydown
  // listener on every render of the whole app tree.
  const cancel = useCallback(() => settle(false), [settle]);
  useEscapeToClose(cancel, !!state);

  const opts = state?.opts || {};
  const {
    title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
    destructive = false, requireText = null,
  } = opts;
  const canConfirm = !requireText || typed.trim() === requireText;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="modal-overlay cfm-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) settle(false); }}
        >
          <div className="modal-content modal-content-sm cfm-modal" role="alertdialog" aria-modal="true"
               aria-labelledby="cfm-title" aria-describedby="cfm-message">
            {/* Navy gradient header + white text — DESIGN_SYSTEM.md, no exceptions. */}
            <div className="modal-header">
              <h3 id="cfm-title">{title || (destructive ? 'Please confirm' : 'Confirm')}</h3>
              <button className="modal-close" onClick={() => settle(false)} aria-label="Cancel">&times;</button>
            </div>
            <div className="cfm-body">
              <p id="cfm-message" className="cfm-message">{message}</p>
              {requireText && (
                <div className="cfm-requireText">
                  <label className="form-label" htmlFor="cfm-typed">
                    Type <strong>{requireText}</strong> to continue
                  </label>
                  <input
                    id="cfm-typed"
                    className="form-input"
                    value={typed}
                    autoFocus
                    onChange={(e) => setTyped(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) settle(true); }}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer cfm-footer">
              <button type="button" className="btn btn-secondary" onClick={() => settle(false)}>
                {cancelLabel}
              </button>
              <button
                type="button"
                className={destructive ? 'btn cfm-destructiveBtn' : 'btn btn-primary'}
                disabled={!canConfirm}
                autoFocus={!requireText}
                onClick={() => settle(true)}
              >
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
